import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTwoFactorVerify } from '../hooks/useLogin.js';
import Button from '../components/Button.jsx';
import FormField from '../components/FormField.jsx';

export default function TwoFactorVerifyPage({ preauthToken }) {
  const navigate = useNavigate();
  const location = useLocation();
  const token = preauthToken ?? location.state?.preauth_token;
  const [code, setCode] = useState('');
  const verifyMutation = useTwoFactorVerify();
  const inputRef = useRef(null);

  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }
    inputRef.current?.focus();
  }, [token, navigate]);

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      await verifyMutation.mutateAsync({ preauth_token: token, code });
      navigate('/', { replace: true });
    } catch (err) {
      const status = err?.status;
      const msg = err?.message ?? '';
      // 400 with "non initialisée" means user hasn't set up TOTP yet
      if (status === 400 && msg.toLowerCase().includes('non initialisée')) {
        navigate('/2fa/setup', { state: { preauth_token: token }, replace: true });
        return;
      }
      setCode('');
      inputRef.current?.focus();
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <h1 className="text-xl font-bold text-slate-900">Vérification 2FA</h1>
          <p className="text-sm text-slate-500 mt-1">
            Entrez le code de votre application d'authentification.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4"
          aria-label="Formulaire de vérification 2FA"
        >
          {verifyMutation.error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
              {verifyMutation.error.message ?? 'Code invalide. Réessayez.'}
            </div>
          )}

          <FormField label="Code à 6 chiffres" htmlFor="totp" required>
            <input
              id="totp"
              ref={inputRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="block w-full rounded-lg border border-slate-300 px-3 py-3 text-center text-xl tracking-widest font-mono focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500"
              placeholder="000000"
              required
            />
          </FormField>

          <Button
            type="submit"
            className="w-full"
            loading={verifyMutation.isPending}
            disabled={code.length < 6}
          >
            Accéder au dashboard
          </Button>

          <p className="text-center text-xs text-slate-400">
            Problème ?{' '}
            <a href="/login" className="underline text-slate-600">
              Recommencer
            </a>
          </p>
        </form>
      </div>
    </div>
  );
}
